import { Link } from 'react-router-dom';
import { CalendarCheck, Users, CreditCard, ShieldCheck } from 'lucide-react';
import { useActivityStore } from '../stores/activityStore';
import { useEffect } from 'react';
import PopularStages from '../components/home/PopularStages';
import NewsletterSection from '../components/home/NewsletterSection';
import AvisGoogleSection from '../components/home/AvisGoogleSection';
import PhotoReglage from '../components/common/PhotoReglage';
import { useReglages } from '../lib/reglages/useReglages';

const HomePage = () => {
  /*
    `activities` et `isLoading` etaient destructures sans jamais servir : c'est
    `PopularStages` qui lit le magasin. L'appel a `fetchActivities` reste, lui,
    parce qu'il remplit ce magasin pour ce composant-la.
  */
  const { fetchActivities } = useActivityStore();
  const reglages = useReglages();
  
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);
  
  return (
    <div className="page-transition">
      {/* Hero section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-50 py-16 md:py-24">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 text-center md:text-left">
              <h1 className="text-5xl md:text-6xl leading-tight text-gray-900 mb-6">
                Stages et plaines de vacances pour enfants à Bruxelles
              </h1>
              {/*
                Le titre nommait « des stages » et rien d'autre. Éclosion
                organise deux choses distinctes — des plaines de vacances, aux
                activités variées, et des stages à thème — et une page d'accueil
                qui n'en nomme qu'une se prive de la moitié des recherches.
              */}
              <p className="text-lg text-gray-600 mb-8">
                Éclosion ASBL organise des plaines de vacances et des stages à thème pour
                enfants, dans plusieurs communes bruxelloises, pendant les congés scolaires.
                Nature, cirque, sciences, cuisine, sport et bien plus encore.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center md:justify-start">
                {/*
                  Voir le catalogue AVANT de se connecter : c'est la demande du
                  11 septembre 2026, et c'est aussi ce qui donne aux moteurs un
                  lien depuis l'accueil vers les pages indexables. Sans lui,
                  `/stages` n'existe que dans le sitemap.
                */}
                <Link
                  to="/stages"
                  className="btn-primary text-center py-3 px-6"
                >
                  Voir les stages et plaines
                </Link>
                <Link
                  to="/login"
                  className="btn text-center py-3 px-6 border-2 border-primary-600 text-primary-700 font-semibold hover:bg-primary-50 focus:ring-primary-500"
                >
                  Se connecter
                </Link>
              </div>
              
              <div className="mt-4 text-center md:text-left">
                <p className="text-gray-600 text-sm">
                  Pas encore de compte ?{' '}
                  <Link
                    to="/register"
                    className="text-primary-600 hover:text-primary-700 font-medium underline"
                  >
                    Créer un compte
                  </Link>
                </p>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <PhotoReglage
                photo={reglages?.image_accueil}
                className="aspect-[4/3] w-full rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Popular Stages section */}
      <PopularStages />

      {/*
        Les avis juste après les stages : la note rassure au moment de choisir,
        sans repousser les stages vers le bas. La section disparait tant
        qu'aucun avis n'est saisi.
      */}
      {reglages && <AvisGoogleSection avis={reglages.avis_google} />}
      
      {/* Features section */}
      <section className="py-16">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comment ça marche ?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un processus simple et rapide pour inscrire vos enfants à nos stages
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-full mb-5">
                <Users className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Créez un compte</h3>
              <p className="text-gray-600">
                Inscrivez-vous et ajoutez les profils de vos enfants avec leurs informations
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-full mb-5">
                <CalendarCheck className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Choisissez un stage</h3>
              <p className="text-gray-600">
                Parcourez notre catalogue et sélectionnez les stages qui intéressent vos enfants
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-full mb-5">
                <CreditCard className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Paiement sécurisé</h3>
              <p className="text-gray-600">
                Effectuez le paiement via notre plateforme sécurisée en toute confiance
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center p-3 bg-primary-100 rounded-full mb-5">
                <ShieldCheck className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Confirmation</h3>
              <p className="text-gray-600">
                Recevez votre confirmation d'inscription et les informations pratiques
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Newsletter Section */}
      <NewsletterSection photo={reglages?.image_newsletter} />
    </div>
  );
};

export default HomePage;